import { EpJsConfig } from './models/config.model';
import { BpStorage } from './util/storage';
import { ComponentGenerator } from './services/component-generator/component-generator.service';
import { Analytics } from './modules/analytics/analytics';
import { EpApi } from './modules/api/ep-api';
import { EpLogger } from './util/ep-logger';

import { autoInjectable, container, registry } from 'tsyringe';
import { TextPlacementEffects } from './effects/text-placement.effects';
import { OverlayEffects } from './effects/overlay-effects.effect';
import { AnalyticsEffects } from './effects/analytics.effects';
import { PublicEventEmitterService } from './services/public-event-emitter/public-event-emitter.service';
import { FmcEffects } from './effects/fmc.effects';
import { BrandRtps, FetchRtpsMockOptions, RTPS_OVERLAY_CONTENT_ID } from './models/rtps.model';
import { EpStore } from './services/store/ep-store';
import { RtpsEffects } from './effects/rtps.effects';
import { PluginActions, RtpsActions } from './actions';
import { CryptoUtilService } from './services/crypto/crypto-util.service';
import { ConfigUtilService } from './services/config/config-util.service';
import { QueryParamHelperUtil } from './util/query-param-helper/query-param-helper.util';
import { assignDefined } from './models/util.model';
import { PlacementEffects } from './effects/placement.effects';
import { PluginEffects } from './effects/plugin.effects';

@autoInjectable()
@registry([
  {
    token: Analytics,
    useClass: Analytics,
    options: { singleton: true }
  },
  {
    token: EpApi,
    useClass: EpApi,
    options: { singleton: true }
  }
])
export class EpJs {
  get config(){
    return this.configUtilService.getConfig();
  }
  componentGenerator: ComponentGenerator;
  VERSION: string;
  effectsSubscribed = false;

  constructor(
    public analyticsClient?: Analytics,
    public storage?: BpStorage,
    public api?: EpApi,
    public logger?: EpLogger,
    protected publicEventEmitter?: PublicEventEmitterService,
    private store?: EpStore,
    private configUtilService?: ConfigUtilService
  ) {
  }

  /*
   * - Store the config passed in.
   * - Fetch and insert EpJs stylesheet
   * - Check if the user has an existing tracking_id in long lived storage
   * (fail gracefully if in private mode)
   * - Collect information about third party platform and store in session
   * - Check if there is merchant data stored for this session
   * - - If merchant data exists: create the cta links and placements
   * - - - Track number of placements / cta links created to analyticsClient
   * - - If merchant data does not exist: call merchant data api endpoint,
   * including the tracking_id if available.
   * - - - On success, create the placements and cta links as done above
   *
   * */
  async init(config: Partial<EpJsConfig>) {
    this.updateConfig(config);

    this.componentGenerator = container.resolve(ComponentGenerator);
    this.setupSideEffects();

    /*
     * Init analyticsClient
     * If tracking ID not found generate one that
     * will be sent in with the first api call.
     * */
    this.analyticsClient.init();
    await this.api.init();
    this.store.dispatch(PluginActions.initPlugins());
    this.publicEventEmitter.emitter.emit('event:ready');

    /*
     * Generate placements
     * */
    await this.generatePlacements();

    this.publicEventEmitter.emitter.emit('event:placements-generated');

    if (this.config.onReady) {
      this.config.onReady();
    }
  }

  updateConfig(config: Partial<EpJsConfig>, replace = false) {
    if (replace) {
      this.configUtilService.replaceConfig(config);
    } else {
      this.configUtilService.updateConfig(config);
    }
  }

  /*
  * Subscribe all side effects
  * */
  setupSideEffects() {
    if (this.effectsSubscribed) {
      return;
    }
    container.resolve(TextPlacementEffects).bindSideEffects();
    container.resolve(PlacementEffects).bindSideEffects();
    container.resolve(OverlayEffects).bindSideEffects();
    container.resolve(AnalyticsEffects).bindSideEffects();
    container.resolve(FmcEffects).bindSideEffects();
    container.resolve(RtpsEffects).bindSideEffects();
    container.resolve(PluginEffects).bindSideEffects();

    this.effectsSubscribed = true;
  }

  /*
   * Find each predefined target element and replace supported
   * elements with custom EpJs elements.
   * */
  async generatePlacements() {
    await this.componentGenerator.generateAll();
  }

  /*
  * Public event emitter exposed to Brand
  * */
  on(event: string, callback: () => void) {
    return this.publicEventEmitter.on(event, callback);
  }

  /*
  * Public method for Brand to submit RTPS request.
  * */
  async submitRTPS(customerInfo: BrandRtps, mockResponse?: FetchRtpsMockOptions): Promise<string> {
    const attribution = QueryParamHelperUtil.getAttributionQueryParams();
    customerInfo = assignDefined({}, attribution, customerInfo);
    const customerInfoHash = await CryptoUtilService.getObjectHash(customerInfo);

    this.store.dispatch(RtpsActions.brandSubmitRTPS(customerInfo, mockResponse));

    return customerInfoHash;
  }
}
